'use client';

import { ServiceLayer } from '@packages/modules/src/ServiceLayer/ServiceLayer.client';
import type { FragmentType } from '@packages/gql/generated/shopping';
import type { LegalHintsItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/LegalHintsItemFragmentFragmentDoc';
import { useLayerTracking } from '@packages/modules/src/Tracking/hooks/useLayerTracking/useLayerTracking';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { dispatchDisplayOverlayTracking } from '@packages/shared/src/ui/Dialog/dispatchDialogOverlayTracking';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { startTransition, useState } from 'react';
import { unmask } from '@packages/gql/src/betterMasking';
import { useServiceLayerTracking } from '../Tracking/hooks/useServiceLayerTracking';

/*
 * INFO: As of right now the data structure for productHints and legalHints is the same, so the components are mostly identical.
 * The data for these two might change in the future and then the two components would have to be split again. For this reason we decided to split into seperate cards them from the start.
 */

/* GraphQL */ `
  fragment LegalHintsItemFragment on Hint {
    iconUrl
    text
    serviceLayerId
    dialogContent
  }
`;

export type LegalHintsItemProps = {
  legalHintsData: FragmentType<typeof LegalHintsItemFragmentFragmentDoc>;
};

/**
 * LegalHintsItem component displays a legal hint
 * */
export const LegalHintsItem = ({ legalHintsData }: LegalHintsItemProps) => {
  const dispatchGTMEvent = useTracking();
  const { iconUrl, serviceLayerId, text, dialogContent } =
    unmask<typeof LegalHintsItemFragmentFragmentDoc>(legalHintsData);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { trackViewLayer } = useLayerTracking();
  const { trackServiceLayerClick } = useServiceLayerTracking();

  const handleClick = () => {
    trackViewLayer(text);
    // handler can only be dispatched if a serviceLayerId is present
    if (serviceLayerId) trackServiceLayerClick(serviceLayerId);
    dispatchDisplayOverlayTracking({
      eventDispatcher: dispatchGTMEvent,
      displayTrackingProps: { category: 'dv-service-layer-legal-hints-item', label: text },
    });
    startTransition(() => setIsDialogOpen(true));
  };

  return (
    <>
      <button
        type="button"
        className="flex w-full items-center gap-1 text-start"
        onClick={() => handleClick()}
        disabled={!serviceLayerId && !dialogContent}
      >
        {imageError ? (
          <span className="icon-emp-service size-3" />
        ) : (
          <Image width={24} height={24} alt="" src={iconUrl} onError={() => setImageError(true)} />
        )}
        <span className="text-size-body-sm">{text}</span>
      </button>
      {serviceLayerId !== null && (
        <Dialog isOpen={isDialogOpen} onClose={() => startTransition(() => setIsDialogOpen(false))}>
          <DialogHeader onClose={() => startTransition(() => setIsDialogOpen(false))}>
            {text}
          </DialogHeader>
          <DialogContent>
            {dialogContent ? (
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: dialogContent }}
                className="p-2"
              />
            ) : (
              <ServiceLayer serviceLayerId={serviceLayerId} />
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
